import { ComponentType } from "react";
import { Redirect, Route, Switch } from "react-router-dom";
import { Header } from "../components/Header";
import { Sidebar } from "../components/Sidebar";
import { BalancePage } from "../pages/balance";
import { BalanceDetailPage } from "../pages/balance/detail";
import { BalanceHistoryPage } from "../pages/balance/history";
import { BondPage } from "../pages/bond";
import { ConfigPage } from "../pages/Config";
import { DashboardPage } from "../pages/Dashboard";
import { FAQPage } from "../pages/faq";
import { ImagePage } from "../pages/Image";
import { KYCPage } from "../pages/kyc/KYC";
import { LoginPage } from "../pages/Login";
import { LogoutPage } from "../pages/Logout";
import { NotfoundPage } from "../pages/Notfound";
import { OrderPage } from "../pages/Order";
import { OrderListPage } from "../pages/orderlist";
import { ConfirmOrderListPage } from "../pages/orderlist/confirm";
import { PortfolioPage } from "../pages/portfolio";
import { AddPortfolioPage } from "../pages/portfolio/create";
import { PortfolioDetailPage } from "../pages/portfolio/detail";
import { SavingPage } from "../pages/saving";
import { StockPage } from "../pages/stock";
import { SurveyPage } from "../pages/survey";
import { AddSurveyPage } from "../pages/survey/create";
import { SurveyDetailPage } from "../pages/survey/detail";
import { UserPage } from "../pages/user";
import { isLoggedIn } from "../utils/auth";
import { AddUserPage } from "../pages/user/add";
import { EditUserPage } from "../pages/user/edit";
import { BondAddPage } from "../pages/bond/add";
import { KycDetailsPage } from "../pages/kyc/detail/index";
import { EditBondsPage } from "../pages/bond/edit";
import { BondDetailPage } from "../pages/bond/detail";
import { AddSavingsPage } from "../pages/saving/add";
import { EditSavingsPage } from "../pages/saving/edit";
import { AddStockPage } from "../pages/stock/add";
import { EditStockPage } from "../pages/stock/edit";
import { BalanceDetailsStockHistoryPage } from "../pages/balance/detail/stock";
import { BalanceDetailsBondHistoryPage } from "../pages/balance/detail/bond";
import { FAQEditPage } from "../pages/faq/Edit";

import { GroupDetailsPage } from "../pages/user/group-details";
import { BalanceDetailsSavingsHistoryPage } from "../pages/balance/detail/saving";
import { UserDeletionRequestPage } from "../pages/userDeletionRequest";
import { ActivityPage } from "../pages/activity";
import { AddActivityPage } from "../pages/activity/add";
import { CreateOrderListPage } from "../pages/orderlist/create";

import { FeeReportsPage } from "@/pages/reports/Fee";
import { InterestPayoutReportsPage } from "@/pages/reports/InterestPayout";
import { CorrectionReportsPage } from "@/pages/reports/Correction";
import { HoldReleaseReportsPage } from "@/pages/reports/HoldRelease";

import { StockDividendsPage } from "@/pages/stock/dividends";
import { CreateStockDividendPage } from "@/pages/stock/dividends/create";

import { ContractPage } from "@/pages/kyc/contract";
import { RefundPage } from "@/pages/refund";
import { RefundCreatePage } from "@/pages/refund/create/RefundCreate";
import { RefundConfirm } from "@/pages/refund/confirm";
import { RefundListPage } from "@/pages/refund/list";
import { RefundCalculationPage } from "@/pages/refund/calculation";
import { RefundDetail } from "@/pages/refund/detail";
import { RefundReportPage } from "@/pages/refund/report";
import { SuggestPage } from "@/pages/suggest";
import { SuggestAddPage } from "@/pages/suggest/add";
import { OperatorRefundPage } from "@/pages/refund/operatorIndex";
import { MSCDAccountPage } from "@/pages/mscdaccount";
import { MonthlyFeePage } from "@/pages/monthlyfee";
import { HuulgaPage } from "@/pages/huulga";
import { UnmatchedDepositsPage } from "@/pages/UnmatchedDeposits";

import { KycQuestionnairePage } from "@/pages/kycQuestionnaire";
import { KycQuestionnaireCreatePage } from "@/pages/kycQuestionnaire/create";
import { KycQuestionnaireDetailPage } from "@/pages/kycQuestionnaire/detail";
import { KycQuestionnaireSendPage } from "@/pages/kycQuestionnaire/send";
import { KycQuestionnaireSendDetailPage } from "@/pages/kycQuestionnaire/sendDetail";

import { useAppRole } from "@/hooks/usePermission";
import {
  AppRole,
  PageKey,
  PERMITTED_BACKEND_ROLES,
  canEdit,
  canView,
} from "@/utils/permissions";
import { getUserData } from "@/utils/auth";

type RouteEntry = {
  path: string;
  component: ComponentType<any>;
  pageKey: PageKey;
  exact?: boolean;
  requiresEdit?: boolean;
  componentByRole?: Partial<Record<AppRole, ComponentType<any>>>;
};

const ROUTES: RouteEntry[] = [
  // Dashboard
  { path: "/", component: DashboardPage, pageKey: "dashboard", exact: true },

  // Customers
  { path: "/users", component: UserPage, pageKey: "users-list", exact: true },
  {
    path: "/users/add",
    component: AddUserPage,
    pageKey: "users-list",
    exact: true,
    requiresEdit: true,
  },
  {
    path: "/users/edit/:_id",
    component: EditUserPage,
    pageKey: "users-list",
    exact: true,
    requiresEdit: true,
  },
  {
    path: "/users/group-details/:_id",
    component: GroupDetailsPage,
    pageKey: "users-list",
    exact: true,
  },
  {
    path: "/userdeletionrequests",
    component: UserDeletionRequestPage,
    pageKey: "user-deletion-request",
    exact: true,
  },

  // Verification
  { path: "/kycs", component: KYCPage, pageKey: "kyc", exact: true },
  {
    path: "/kycs/:_id",
    component: KycDetailsPage,
    pageKey: "kyc",
    exact: true,
  },
  {
    path: "/contracts",
    component: ContractPage,
    pageKey: "contracts",
    exact: true,
  },
  {
    path: "/mscdaccounts",
    component: MSCDAccountPage,
    pageKey: "mscd-accounts",
    exact: true,
  },
  {
    path: "/kyc-questionnaires",
    component: KycQuestionnairePage,
    pageKey: "kyc-questionnaire",
    exact: true,
  },
  {
    path: "/kyc-questionnaires/create",
    component: KycQuestionnaireCreatePage,
    pageKey: "kyc-questionnaire",
    exact: true,
    requiresEdit: true,
  },
  {
    path: "/kyc-questionnaires/edit/:_id",
    component: KycQuestionnaireCreatePage,
    pageKey: "kyc-questionnaire",
    exact: true,
    requiresEdit: true,
  },
  {
    path: "/kyc-questionnaires/detail/:_id",
    component: KycQuestionnaireDetailPage,
    pageKey: "kyc-questionnaire",
    exact: true,
  },
  {
    path: "/kyc-questionnaires/send/:_id",
    component: KycQuestionnaireSendPage,
    pageKey: "kyc-questionnaire",
    exact: true,
    requiresEdit: true,
  },
  {
    path: "/kyc-questionnaires/send-detail/:_id",
    component: KycQuestionnaireSendDetailPage,
    pageKey: "kyc-questionnaire",
    exact: true,
  },

  // Portfolio
  { path: "/bonds", component: BondPage, pageKey: "bonds", exact: true },
  {
    path: "/bonds/add",
    component: BondAddPage,
    pageKey: "bonds",
    exact: true,
    requiresEdit: true,
  },
  {
    path: "/bonds/edit/:_id",
    component: EditBondsPage,
    pageKey: "bonds",
    exact: true,
    requiresEdit: true,
  },
  {
    path: "/bonds/detail/:_id",
    component: BondDetailPage,
    pageKey: "bonds",
    exact: true,
  },
  { path: "/stocks", component: StockPage, pageKey: "stocks", exact: true },
  {
    path: "/stocks/add",
    component: AddStockPage,
    pageKey: "stocks",
    exact: true,
    requiresEdit: true,
  },
  {
    path: "/stocks/edit/:_id",
    component: EditStockPage,
    pageKey: "stocks",
    exact: true,
    requiresEdit: true,
  },
  { path: "/savings", component: SavingPage, pageKey: "savings", exact: true },
  {
    path: "/savings/add",
    component: AddSavingsPage,
    pageKey: "savings",
    exact: true,
    requiresEdit: true,
  },
  {
    path: "/savings/edit/:_id",
    component: EditSavingsPage,
    pageKey: "savings",
    exact: true,
    requiresEdit: true,
  },
  {
    path: "/portfolios",
    component: PortfolioPage,
    pageKey: "portfolios",
    exact: true,
  },
  {
    path: "/portfolios/:_id",
    component: AddPortfolioPage,
    pageKey: "portfolios",
    exact: true,
    requiresEdit: true,
  },
  {
    path: "/portfoliodetail/:_id",
    component: PortfolioDetailPage,
    pageKey: "portfolios",
    exact: true,
  },

  // Investments
  { path: "/orders", component: OrderPage, pageKey: "orders", exact: true },
  {
    path: "/orderlists",
    component: OrderListPage,
    pageKey: "orderlists",
    exact: true,
  },
  {
    path: "/orderlists/create",
    component: CreateOrderListPage,
    pageKey: "orderlists",
    exact: true,
    requiresEdit: true,
  },
  {
    path: "/orderlist/:_id",
    component: ConfirmOrderListPage,
    pageKey: "orderlists",
    exact: true,
  },
  {
    path: "/balances",
    component: BalancePage,
    pageKey: "balances",
    exact: true,
  },
  {
    path: "/balancedetail/:_id",
    component: BalanceDetailPage,
    pageKey: "balances",
    exact: true,
  },
  {
    path: "/balancedetail/stock/:_id/:index",
    component: BalanceDetailsStockHistoryPage,
    pageKey: "balances",
    exact: true,
  },
  {
    path: "/balancedetail/bond/:_id/:index",
    component: BalanceDetailsBondHistoryPage,
    pageKey: "balances",
    exact: true,
  },
  {
    path: "/balancedetail/savings/:_id/:index",
    component: BalanceDetailsSavingsHistoryPage,
    pageKey: "balances",
    exact: true,
  },
  {
    path: "/balanceHistory/:_id",
    component: BalanceHistoryPage,
    pageKey: "balances",
    exact: true,
  },
  { path: "/huulga", component: HuulgaPage, pageKey: "huulga", exact: true },
  {
    path: "/stocks/dividends",
    component: StockDividendsPage,
    pageKey: "stock-dividends",
    exact: true,
  },
  {
    path: "/stocks/dividends/create",
    component: CreateStockDividendPage,
    pageKey: "stock-dividends",
    exact: true,
    requiresEdit: true,
  },
  {
    path: "/unmatched-deposits",
    component: UnmatchedDepositsPage,
    pageKey: "unmatched-deposits",
    exact: true,
  },

  // Refund (Operator gets specialized OperatorRefundPage)
  {
    path: "/refund",
    component: RefundPage,
    componentByRole: { OPERATOR: OperatorRefundPage },
    pageKey: "refund-orders",
    exact: true,
  },
  {
    path: "/refund/create",
    component: RefundCreatePage,
    pageKey: "refund-orders",
    exact: true,
    requiresEdit: true,
  },
  {
    path: "/refund/confirm/:_id",
    component: RefundConfirm,
    pageKey: "refund-orders",
    exact: true,
    requiresEdit: true,
  },
  {
    path: "/refund/report/:_id",
    component: RefundReportPage,
    pageKey: "refund-orders",
    exact: true,
  },
  {
    path: "/refund/detail/:_id",
    component: RefundDetail,
    pageKey: "refund-orders",
    exact: true,
  },
  {
    path: "/refund/list",
    component: RefundListPage,
    pageKey: "refund-list",
    exact: true,
  },
  {
    path: "/refund/calculation",
    component: RefundCalculationPage,
    pageKey: "refund-calculation",
    exact: true,
  },

  // Reports
  {
    path: "/reports/interest-payout",
    component: InterestPayoutReportsPage,
    pageKey: "reports-interest",
    exact: true,
  },
  {
    path: "/reports/fee",
    component: FeeReportsPage,
    pageKey: "reports-fee",
    exact: true,
  },
  {
    path: "/monthlyfee",
    component: MonthlyFeePage,
    pageKey: "reports-monthly-fee",
    exact: true,
  },
  {
    path: "/reports/corrections",
    component: CorrectionReportsPage,
    pageKey: "reports-corrections",
    exact: true,
  },
  {
    path: "/reports/hold-release",
    component: HoldReleaseReportsPage,
    pageKey: "reports-hold-release",
    exact: true,
  },

  // Other
  { path: "/surveys", component: SurveyPage, pageKey: "surveys", exact: true },
  {
    path: "/surveys/:_id",
    component: AddSurveyPage,
    pageKey: "surveys",
    exact: true,
    requiresEdit: true,
  },
  {
    path: "/surveydetail/:_id",
    component: SurveyDetailPage,
    pageKey: "surveys",
    exact: true,
  },
  { path: "/faqs", component: FAQPage, pageKey: "faq", exact: true },
  { path: "/faqs/:id", component: FAQEditPage, pageKey: "faq" },
  { path: "/configs", component: ConfigPage, pageKey: "configs", exact: true },
  { path: "/images", component: ImagePage, pageKey: "images", exact: true },
  {
    path: "/activity",
    component: ActivityPage,
    pageKey: "activity",
    exact: true,
  },
  {
    path: "/activity/add",
    component: AddActivityPage,
    pageKey: "activity",
    exact: true,
    requiresEdit: true,
  },
  { path: "/suggest", component: SuggestPage, pageKey: "suggest", exact: true },
  {
    path: "/suggest/add",
    component: SuggestAddPage,
    pageKey: "suggest",
    exact: true,
    requiresEdit: true,
  },
];

function getAccessibleRoutes(role: AppRole): RouteEntry[] {
  return ROUTES.filter((entry) => {
    if (!canView(role, entry.pageKey)) return false;
    if (entry.requiresEdit && !canEdit(role, entry.pageKey)) return false;
    return true;
  });
}

export const AdminApp = () => {
  const userLoggedIn = isLoggedIn();
  const role = useAppRole();
  const backendRole = getUserData()?.role;

  if (
    !userLoggedIn ||
    !role ||
    !backendRole ||
    !PERMITTED_BACKEND_ROLES.has(backendRole)
  ) {
    return (
      <>
        <Route component={LoginPage} path="/login" />
        <Redirect to="/login" />
      </>
    );
  }

  const accessible = getAccessibleRoutes(role);

  return (
    <div className="container">
      <Sidebar />
      <div className="container-body">
        <Header />
        <div className="container-content">
          <Switch>
            {accessible.map((entry) => {
              const Component =
                entry.componentByRole?.[role] ?? entry.component;
              return (
                <Route
                  key={`${entry.path}|${entry.pageKey}|${
                    entry.requiresEdit ? "edit" : "view"
                  }`}
                  exact={entry.exact}
                  component={Component}
                  path={entry.path}
                />
              );
            })}
            <Route exact component={LogoutPage} path="/logout" />
            <Route component={NotfoundPage} />
          </Switch>
        </div>
      </div>
    </div>
  );
};
